import { Component, Input, OnChanges, ViewEncapsulation } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ColorsConfig, ViewCategory } from '@ddt-web/config';
import { ComponentData, TabData } from '@ddt-web/data-service';
import { CommonDataUtil, IHighChartsAttributesModel, IScenarioAnalysisData, MultiChartModel, StackedDataUtil } from '@ddt-web/highchart-manager';

import { ScenarioAnalysisConfig } from '../../constants';
import { IScenarioInfo } from '../../models/scenario-info.interface';

@Component({
	selector: 'sc-scenario-analysis',
	templateUrl: './scenario-analysis.component.html',
	styleUrls: ['./scenario-analysis.component.scss'],
	encapsulation: ViewEncapsulation.None
})

/**
 * ScenarioAnalysisContainerComponent: this component wraps both the mobile and desktop components for
 * the scenario analysis chart section.
 */
export class ScenarioAnalysisComponent implements OnChanges {
	@Input() public data: ComponentData;
	@Input() public chartMetaInfo: Array<MultiChartModel>;
	@Input() public portfolioIndex: number;
	@Input() public colors: Array<string>;

	public isScenarioAnalysisDetailsModalVisible: boolean = false;
	public scenarioModalInfo: Array<IScenarioInfo> = null;
	public displayPortfolios: any;
	public portfolioSchemas: Array<{ name: string; colors: Array<string> }>;
	public scenarioAnalysisTabIndex: number;
	private transformedData: IScenarioAnalysisData;

	public ViewCategory = ViewCategory;

	constructor(private _contentService: ContentService) { }

	public ngOnChanges(): void {
		if (this.data.success && !this.data.getTabById('scenarios.analysis').error) {
			this.scenarioAnalysisTabIndex = this.data.tabs.findIndex(tab => tab.config.id === 'scenarios.analysis');
			this.transformedData = StackedDataUtil.getStackedData(this.data.getTabById('scenarios.analysis'));
			const scenarioAnalysisTab = this.data.getTabById('scenarios.analysis');
			this.getScenarioModalInfo(scenarioAnalysisTab);
			if (CommonDataUtil.hasDuplicateNames(this.transformedData.portfolios)) {
				this.transformedData.portfolios = CommonDataUtil.createUniqueNames(this.transformedData.portfolios);
			}
			this.portfolioSchemas = this.transformedData.portfolios.map((portfolio, index) => {
				return { name: portfolio, colors: ColorsConfig.SCENARIO_ANALYSIS_COLORS[index] };
			});
			this.displayPortfolios = this.portfolioSchemas;
			this.setColors();
			this.chartMetaInfo = new Array<MultiChartModel>();
			let chartModel = null;
			this.transformedData.scenarios.forEach((scenario, index) => {
				const type = index === 0 ? 'stacked-group-top-bar' : 'stacked-group-bar';
				const highChartsAttributesModel: IHighChartsAttributesModel = {
					tabData: this.data.tabs[this.scenarioAnalysisTabIndex],
					colors: this.colors,
					chartType: type,
					portfolioIndex: this.portfolioIndex,
					customAttributes: { categoryFilter: scenario }
				};
				chartModel = new MultiChartModel(highChartsAttributesModel);
				this.chartMetaInfo.push(chartModel);
			});
		}
	}

	/**
	 * portfolioSelected: event handler for the mat-select DOM object.
	 * @param {Event} e: the event.
	 */
	public portfolioSelected(e): void {
		this.portfolioIndex = Number(e.target.value);
		if (this.portfolioIndex > -1) {
			this.displayPortfolios = [this.portfolioSchemas[this.portfolioIndex]];
		} else {
			this.displayPortfolios = this.portfolioSchemas;
		}
		const updatedChartMetaInfo = [];
		this.setColors();
		this.chartMetaInfo.forEach(chartInfo => {
			chartInfo.highChartsAttributes.portfolioIndex = this.portfolioIndex;
			chartInfo.highChartsAttributes.colors = this.colors;
			updatedChartMetaInfo.push(new MultiChartModel(chartInfo.highChartsAttributes));
		});

		this.chartMetaInfo = updatedChartMetaInfo;
	}

	/**
	 * openScenarioDetails: opens the scenario analysis details modal.
	 */
	public openScenarioDetails(): void {
		this.isScenarioAnalysisDetailsModalVisible = true;
	}

	/**
	 * setColors: sets the colors to be used by each portfolio.
	 */
	private setColors(): void {
		this.colors = this.displayPortfolios
			.map(displayPortfolio => displayPortfolio.colors)
			.join() // flatten the multiple arrays
			.split(','); // join them back together into a single array
		this.colors.reverse();
	}

	/**
	 * getScenarioModalInfo: gets the data for the scenario analysis info modal.
	 */
	private getScenarioModalInfo(tab: TabData): void {
		const headerKeys = tab.tableData[0].headersX.filter(header => header.config.id === 'scenario').map(header => header.rawValue);
		this.scenarioModalInfo = new Array<IScenarioInfo>();
		headerKeys.forEach(key => {
			this.scenarioModalInfo.push({
				subTitle: ScenarioAnalysisConfig[`${key}_INFO_SUBTITLE`],
				subTitleKey: `${key}_INFO_SUBTITLE`,
				description: ScenarioAnalysisConfig[`${key}_INFO_DESCRIPTION`],
				descriptionKey: `${key}_INFO_DESCRIPTION`
			});
		});
	}
}
